// Pure helpers for the Network Context / Risk Explanation feature (PublicRisk.networkContext/summaryCode/
// keyFindings/signalExplanations — see NetRiskScan.Domain.Rules.RiskExplanationEngine) — split out from
// components the same way utils/riskSignals.ts is, so this stays unit-testable without rendering.
import type { getStrings } from "../i18n/strings";
import type { KeyFinding, PublicRisk, SignalExplanation } from "../types/dto";
import { signalLabel } from "./riskSignals";

type Strings = ReturnType<typeof getStrings>;

// Absent networkContext (a share snapshot saved before this feature existed) reads exactly like an
// ordinary, unrecognized address — never crash, never silently claim a recognition that was never made.
const EMPTY_NETWORK_CONTEXT = {
  type: "normal",
  isRecognized: false,
  isSharedInfrastructure: false,
  matchStrength: "none",
  organization: null,
  hostname: null,
} as const;

export function resolveNetworkContext(risk: PublicRisk) {
  return risk.networkContext ?? EMPTY_NETWORK_CONTEXT;
}

const FALLBACK_CONTEXT_TYPE_NAMES: Record<string, string> = {
  normal: "Ordinary address",
  public_dns: "Public DNS Resolver",
  cdn: "CDN Edge Node",
  search_engine_crawler: "Search Engine Crawler",
  security_scanner: "Security Research Scanner",
  internet_measurement: "Internet Measurement Node",
  public_service: "Public Internet Service",
  enterprise_egress: "Enterprise Network Egress",
  shared_cloud_service: "Shared Cloud Service",
};

export function networkContextTypeLabel(type: string, strings: Strings): string {
  return strings.networkContextTypeNames?.[type] ?? FALLBACK_CONTEXT_TYPE_NAMES[type] ?? type;
}

const FALLBACK_SEVERITY_TONE: Record<string, "safe" | "neutral" | "caution" | "elevated" | "danger"> = {
  positive: "safe",
  neutral: "neutral",
  caution: "caution",
  elevated: "elevated",
  severe: "danger",
};

// The one place a SignalExplanation's severity code becomes a CSS tone class — see index.css's
// field--severity-* / dot--severity-* rules for the actual color tokens.
export function severityTone(severity: string | undefined): "safe" | "neutral" | "caution" | "elevated" | "danger" {
  if (!severity) return "neutral";
  return FALLBACK_SEVERITY_TONE[severity] ?? "neutral";
}

export function signalExplanationFor(risk: PublicRisk, signalType: string): SignalExplanation | undefined {
  return risk.signalExplanations?.[signalType];
}

const FALLBACK_SUMMARY_TITLES: Record<string, string> = {
  not_applicable: "Not applicable",
  insufficient_data: "Not enough data",
  insufficient_data_recognized_infrastructure: "Recognized, but not enough data",
  active_risk_detected: "Active risk detected",
  elevated_risk_detected: "Elevated risk detected",
  low_risk_contextualized_infrastructure: "Low overall risk",
  low_risk_with_history: "Low overall risk, with history",
  network_identity_risk_detected: "Anonymous network risk",
  low_risk_recognized_infrastructure: "Low overall risk",
  low_risk_clean: "Low overall risk",
};

const FALLBACK_SUMMARY_BODIES: Record<string, string> = {
  not_applicable: "Public IP risk evaluation does not apply to this address.",
  insufficient_data: "There wasn't enough usable data this time to reach a reliable conclusion.",
  insufficient_data_recognized_infrastructure:
    "This address is recognized as shared public infrastructure, but there wasn't enough risk data this time to reach a reliable conclusion.",
  active_risk_detected: "One or more current, high-confidence threat indicators were found on this address.",
  elevated_risk_detected: "A confirmed finding on this address is worth attention.",
  low_risk_contextualized_infrastructure:
    "No current active threat was found. Some historical or shared-infrastructure-related records exist, but their impact has been reduced because this address is recognized as shared public infrastructure.",
  low_risk_with_history:
    "No current active threat was found. Some historical records exist, but nothing here confirms an ongoing issue.",
  network_identity_risk_detected:
    "This address is confirmed to use anonymizing network infrastructure, which lowers its identity trust for verification, payment, streaming and anti-abuse systems. No attack, abuse, compromise or malware activity was found.",
  low_risk_recognized_infrastructure: "No risk indicators were found, and this address is recognized as shared public infrastructure.",
  low_risk_clean: "No significant risk indicators were found for this address based on currently available data.",
};

export function summaryTitle(summaryCode: string | undefined, strings: Strings): string {
  if (!summaryCode) return strings.riskSummaryTitleFallback ?? FALLBACK_SUMMARY_TITLES.low_risk_clean;
  return strings.riskSummaryTitles?.[summaryCode] ?? FALLBACK_SUMMARY_TITLES[summaryCode] ?? summaryCode;
}

export function summaryBody(summaryCode: string | undefined, strings: Strings): string {
  if (!summaryCode) return strings.riskSummaryBodyFallback ?? FALLBACK_SUMMARY_BODIES.low_risk_clean;
  return strings.riskSummaryBodies?.[summaryCode] ?? FALLBACK_SUMMARY_BODIES[summaryCode] ?? "";
}

const FALLBACK_FINDING_TITLES: Record<string, string> = {
  recognized_infrastructure: "Recognized public infrastructure",
  no_anonymity_detected: "No anonymity network detected",
  no_signals_detected: "No risk signals detected",
};

const FALLBACK_FINDING_BODIES: Record<string, string> = {
  recognized_infrastructure: "This address is recognized as shared public infrastructure.",
  no_anonymity_detected: "No Proxy, VPN, or Tor characteristics were detected.",
  no_signals_detected: "No risk indicators were found for this address.",
};

const FALLBACK_WARNING_FINDING_BODY = "A related record exists, but current evidence does not confirm it as an active, ongoing issue.";
const FALLBACK_NEGATIVE_FINDING_BODY = "This is a current, confirmed finding on this address.";
const FALLBACK_IDENTITY_FINDING_BODY = "This is a confirmed network-identity characteristic of this address, not a finding of malicious activity.";

// KeyFinding.code is either one of the fixed codes above, or a curated signal type (Tor/AttackSource/...)
// reusing the same localized vocabulary the rest of the page already has via signalLabel.
export function keyFindingTitle(finding: KeyFinding, strings: Strings): string {
  const fromFixedCode = strings.keyFindingTitles?.[finding.code] ?? FALLBACK_FINDING_TITLES[finding.code];
  if (fromFixedCode) return fromFixedCode;
  return signalLabel(finding.code, strings);
}

export function keyFindingBody(finding: KeyFinding, strings: Strings): string {
  const fromFixedCode = strings.keyFindingBodies?.[finding.code] ?? FALLBACK_FINDING_BODIES[finding.code];
  if (fromFixedCode) return fromFixedCode;
  if (finding.kind === "warning") return strings.keyFindingWarningBody ?? FALLBACK_WARNING_FINDING_BODY;
  if (finding.kind === "negative") return strings.keyFindingNegativeBody ?? FALLBACK_NEGATIVE_FINDING_BODY;
  if (finding.kind === "identity") return strings.keyFindingIdentityBody ?? FALLBACK_IDENTITY_FINDING_BODY;
  return "";
}

// ==================================================================================================
//  risk-v4.12 — anonymity type, service friction, and the structured "why" (spec §六/§八/§十九)
// ==================================================================================================

// Every table below follows this file's existing convention: a locale's own dictionary wins, English is
// the fallback, and an unrecognized code degrades to the code itself rather than to an empty string or a
// crash. A share snapshot saved before risk-v4.12 simply carries none of these fields, and every helper
// here returns null for that case so the panels disappear rather than rendering half-empty.

const FALLBACK_ANONYMITY_PROFILE_NAMES: Record<string, string> = {
  none: "No anonymizing service detected",
  commercial_vpn: "Commercial VPN",
  shared_vpn: "Shared VPN exit",
  public_proxy: "Public proxy",
  residential_proxy: "Residential proxy",
  unknown_proxy: "Proxy of unknown type",
  tor: "Tor exit node",
  shared_exit: "Shared exit",
  privacy_relay: "Privacy relay",
};

export function anonymityProfileLabel(profile: string | undefined, strings: Strings): string | null {
  if (!profile || profile === "none") return null;
  return strings.anonymityProfileNames?.[profile] ?? FALLBACK_ANONYMITY_PROFILE_NAMES[profile] ?? profile;
}

const FALLBACK_SERVICE_FRICTION_NAMES: Record<string, string> = {
  low: "Low",
  moderate: "Moderate",
  high: "High",
};

// The sentence that makes a moderate Index legible to someone whose experience is "every site
// challenges me". Kept separate from the level name so a surface can render the badge alone.
const FALLBACK_SERVICE_FRICTION_BODIES: Record<string, string> = {
  low: "This address looks like an ordinary consumer or business connection. Websites and online services rarely add extra verification for traffic from it.",
  moderate:
    "Traffic from this address comes from a datacenter or a VPN exit. Nothing here suggests it has done anything wrong, but some websites, payment providers and sign-in flows add CAPTCHAs or extra verification for this kind of network regardless.",
  high: "Traffic from this address comes from a shared or anonymous exit, or the address itself carries an abuse record. Expect outright blocks from some websites, payment providers and sign-in flows, not merely extra verification.",
};

export function serviceFrictionLabel(level: string | undefined, strings: Strings): string | null {
  if (!level) return null;
  return strings.serviceFrictionLevelNames?.[level] ?? FALLBACK_SERVICE_FRICTION_NAMES[level] ?? level;
}

export function serviceFrictionBody(level: string | undefined, strings: Strings): string | null {
  if (!level) return null;
  return strings.serviceFrictionBodies?.[level] ?? FALLBACK_SERVICE_FRICTION_BODIES[level] ?? null;
}

// The single most important line for the reported defect: "this score is about what the address IS, not
// about anything it has DONE" was a sentence NetRiskScan previously had no way to say.
const FALLBACK_PRIMARY_REASON_BODIES: Record<string, string> = {
  "primary_reason:threat_evidence":
    "This score is driven by evidence of recent attack or abuse activity from this address — not by what kind of network it belongs to.",
  "primary_reason:reputation_history":
    "This score is driven by this address's reputation record with abuse-reporting sources, not by what kind of network it belongs to.",
  "primary_reason:historical_record":
    "This score reflects a past record rather than anything happening now. The activity is no longer current, so it is weighted far below a live finding.",
  "primary_reason:network_identity":
    "This score is driven by the kind of network this address belongs to, not by any evidence of malicious activity. No attack, abuse, compromise or malware record was found.",
  "primary_reason:no_risk_found": "No risk evidence was found for this address in any of the sources checked.",
  "primary_reason:insufficient_data": "There wasn't enough usable data this time to say what drives this address's risk.",
};

export function primaryReasonBody(code: string | undefined, strings: Strings): string | null {
  if (!code) return null;
  return strings.riskPrimaryReasonBodies?.[code] ?? FALLBACK_PRIMARY_REASON_BODIES[code] ?? null;
}
